import { FontWeight } from '../types/fitQuizButton/ButtonState';
import {
  BellyShape,
  ChestShape,
  HipShape,
  LengthUnitOfMeasurement,
  WidgetState,
} from '../types/widget.state';


const extractCtaBorderRadius = (css: string) => {
  let regex =
    /\.es-btn-primary {\s*[#aA-zZ0-9-,():\s!;]*(border-radius):(.[0-9]+){1}/;
  let match = css?.match(regex);
  return match && match.length >= 3 ? parseInt(match[2]) : 16;
};

const extractCtaBackgroundColor = (css: string) => {
  let regex =
    /\.es-btn-primary {\s*[#aA-zZ0-9-,():\s!;]*(background-color):(.[#aA-zZ()0-9,]+){1}/;
  let match = css?.match(regex);
  return match && match.length >= 2 ? match[2].toString().trim() : '#0c0706';
};

const extractCtaFontColor = (css: string) => {
  let regex =
    /\.es-btn-primary {\s*[#aA-zZ0-9,()-:\s!;]*(\scolor):(.[#aA-zZ()0-9,]+){1}/;
  let match = css?.match(regex);
  return match && match.length >= 2 ? match[2].toString().trim() : '#ffffff';
};

const extractCtaText = (translations: any) => {
  return translations['ADD_TO_CART'] ?? 'Add to cart';
};

const extractLengthMesurement = (css: string): LengthUnitOfMeasurement => {
  let inchesMain = /\.height__in\s*{\s*font-size:\s*1.75rem;\s*}/;
  let cmMain = /\.height__cm\s*{\s*font-size:\s*1.75rem;\s*}/;
  let inchesMinor = /\.height__in\s*{\s*font-size:\s*1rem;\s*}/;
  let cmMinor = /\.height__cm\s*{\s*font-size:\s*1rem;\s*}/;

  let matchInchesMain = css?.match(inchesMain);
  let matchCmMain = css?.match(cmMain);

  if (matchInchesMain) {
    let matchCmMinor = css?.match(cmMinor);
    return matchCmMinor && matchCmMinor.length >= 1 ? 'inches': 'remove-cm'
  } else if (matchCmMain) {
    let matchInchesMinor = css?.match(inchesMinor);
    return matchInchesMinor && matchInchesMinor.length >= 1 ? 'cm': 'remove-inches'
  }
  return 'inches';
};

const extractCtaBehavior = (feature: any) => {
  return feature && feature['hide_cart_button'] ? 'continue' : 'cart';
};

const extractWidgetBodyFontColor = (css: string) => {
  let regex =
    /\.ant-radio-button-wrapper\s* span\s* {\s*[#aA-zZ0-9-,():\s!;]*(color):(.[#aA-zZ()0-9,]+){1}/;
  let match = css?.match(regex);
  return match && match.length >= 2 ? match[2].toString().trim() : '#000000';
};

const extractWidgetBodyAnswerPadding = (css: string) => {
  const PADDING_CONSTANT = 12;
  let regex =
    /\.es-option--single\s* .ant-radio-button-wrapper\s* {\s*[#aA-zZ0-9-,():\s!;]*(padding):(.[0-9]+)[aA-zZ]*(.[0-9]+)[aA-zZ]*/;
  let match = css?.match(regex);
  return match && match.length > 2
    ? { horizontal: +match[2], vertical: +match[1] }
    : { horizontal: PADDING_CONSTANT, vertical: PADDING_CONSTANT };
};

const extractWidgetBodyAnswerBorderRadius = (css: string) => {
  let regex =
    /\.es-option--single\s* .ant-radio-button-wrapper\s* {\s*[#aA-zZ0-9-,():\s!;]*(border-radius):(.\d+)px/;
  let match = css?.match(regex);
  return match && match.length > 1 ? +match[1] > 0 : true;
};

const extractWidgetBodyTextTransform = (css: string) => {
  let regex =
    /\.ant-radio-button-wrapper\s* span\s*[{#a-zA-Z0-9-:(),.\s!]+{\s*[^}]*text-transform\s*:\s*([a-zA-Z]+)\s*!important\s*;/;
  let match = css?.match(regex);
  return match && match.length >= 2
    ? match[1].toString().trim().toLowerCase() === 'uppercase'
    : false;
};

const extractWidgetBodyFontSize = (css: string) => {
  let regex =
    /\.ant-radio-button-wrapper\s* span\s*[{#a-zA-Z0-9-:(),.\s!]+{\s*[^}]*(font-size)\s*:\s*(\d+)/;
  let match = css?.match(regex);
  return match && match.length >= 2 ? parseInt(match[2]) : 14;
};

const extractWidgetBodyFontWeight = (css: string) => {
  let regex =
    /\.ant-radio-button-wrapper\s* span\s* {\s*[#aA-zZ0-9-,():\s!;]*(font-weight):(.[#aA-zZ()0-9,]+){1}/;
  let match = css?.match(regex);
  return match && match.length >= 2 ? match[2].toString().trim() : '400';
};

const extractWidgetBodyFontFamily = (css: string) => {
  let regex =
    /\.ant-radio-button-wrapper\s* span\s*[#aA-zZ0-9-,():\s!,\.;]+ {\s*[#aA-zZ0-9-,():\s!;]*(font-family):(.[aA-zZ0-9\s]+){1}/;
  let match = css?.match(regex);
  if (!match || match.length < 2) {
    return { fontFamily: '', url: '' };
  }
  let fontFamily = match[2].toString().trim();
  let urlRegex = new RegExp(
    '@font-face\\s*{\\s*font-family:\\s*' +
      fontFamily +
      "\\s*;\\s*src: url\\(('*[a-zA-Z0-9\\/:\\.-]+)'*\\);"
  );
  let urlMatch = css?.match(urlRegex);
  return urlMatch && urlMatch.length >= 2
    ? {
        fontFamily: match[2].toString().trim(),
        url: urlMatch[1].toString().trim(),
      }
    : { fontFamily: '', url: '' };
};

const extractWidgetHeaderFontColor = (css: string) => {
  let regex =
    /\.question-wrapper\s* h6\s* {\s*[#aA-zZ0-9-,():\s!;]*(color):(.[#aA-zZ()0-9,]+){1}/;
  let match = css?.match(regex);
  return match && match.length >= 2 ? match[2].toString().trim() : '#000000';
};

const extractWidgetHeaderFontSize = (css: string) => {
  let regex =
    /\.question-wrapper\s*h6\s*[{#a-zA-Z0-9-:(),.\s!]+{\s*[^}]*(font-size)\s*:\s*(\d+)/;
  let match = css?.match(regex);
  return match && match.length >= 2 ? parseInt(match[2]) : 24;
};

const extractWidgetHeaderFontWeight = (css: string) => {
  let regex =
    /\.question-wrapper\s*h6\s*[{#a-zA-Z0-9-:(),.\s!]+{\s*[^}]*(font-weight)\s*:\s*(\d+)/;
  let match = css?.match(regex);
  return match && match.length >= 2 ? match[2].toString().trim() : '700';
};

const extractWidgetHeaderTextTransform = (css: string) => {
  let regex =
    /\.question-wrapper\s*h6\s*[{#a-zA-Z0-9-:(),.\s!]+{\s*[^}]*text-transform\s*:\s*([a-zA-Z]+)\s*!important\s*;/;
  let match = css?.match(regex);
  return match && match.length >= 2
    ? match[1].toString().trim().toLowerCase() === 'uppercase'
    : false;
};

const extractWidgetHeaderFontFamily = (css: string) => {
  let regex =
    /\.question-wrapper\s* h6\s*[,#aA-zZ0-9-,():\.\s!]+ {\s*[#aA-zZ0-9-,():\s!;]*(font-family)\s*:\s*([a-zA-Z]+)/;
  let match = css?.match(regex);
  if (!match || match.length < 2) {
    return { fontFamily: '', url: '' };
  }
  let fontFamily = match[2].toString().trim();
  let urlRegex = new RegExp(
    '@font-face\\s*{\\s*font-family:\\s*' +
      fontFamily +
      "\\s*;\\s*src: url\\(('*[a-zA-Z0-9\\/:\\.-]+)'*\\);"
  );

  let urlMatch = css?.match(urlRegex);
  let returnValue =
    urlMatch && urlMatch.length >= 2
      ? {
          fontFamily: match[2].toString().trim(),
          url: urlMatch[1].toString().trim(),
        }
      : { fontFamily: '', url: '' };
  return returnValue;
};

const extractWidgetBackground = (css: string) => {
  let regex = /\.card\s*{\s*(background-color):\s*([#aA-zZ0-9]+)/;
  let match = css?.match(regex);
  return match && match.length >= 2 ? match[2].toString().trim() : '#FFFFFF';
};

const extractESWatermarkStatus = (css: string) => {
  let regex = /\.logo a,\s*.es-info-btn\s*{\s*(display):\s*([aA-zZ]+)/;
  let match = css?.match(regex);
  return match && match.length >= 2
    ? match[2].toString().trim().toLowerCase() !== 'none'
    : true;
};

const extractChestShape = (feature: any, gender: 'male' | 'female') => {
  let slim = {
    defaultImageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/chest/male_slim.png'
        : 'https://cdn.easysize.me/images/shapes/chest/female_slim.png',
    imageTitle: 'Slim',
    imageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/chest/male_slim.png'
        : 'https://cdn.easysize.me/images/shapes/chest/female_slim.png',
  };
  let average = {
    defaultImageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/chest/male_average.png'
        : 'https://cdn.easysize.me/images/shapes/chest/female_average.png',
    imageTitle: 'Average',
    imageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/chest/male_average.png'
        : 'https://cdn.easysize.me/images/shapes/chest/female_average.png',
  };
  let broad = {
    defaultImageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/chest/male_broad.png'
        : 'https://cdn.easysize.me/images/shapes/chest/female_broad.png',
    imageTitle: 'Broad',
    imageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/chest/male_broad.png'
        : 'https://cdn.easysize.me/images/shapes/chest/female_broad.png',
  };

  if (
    !Object.keys(feature).includes('U_CHESTSHAPE__MALE') &&
    !Object.keys(feature).includes('U_CHESTSHAPE__FEMALE')
  ) {
    return { slim, average, broad };
  }

  let options = feature[
    gender === 'male' ? 'U_CHESTSHAPE__MALE' : 'U_CHESTSHAPE__FEMALE'
  ]['options'] as any[];

  options.forEach((option) => {
    if (option.key === 'CHEST_1-SLIM') {
      slim.imageUrl = option.image;
    }
    if (option.key === 'CHEST_2-AVERAGE') {
      average.imageUrl = option.image;
    }
    if (option.key === 'CHEST_3-BROAD') {
      broad.imageUrl = option.image;
    }
  });

  return { slim, average, broad };
};

const extractHipShape = (feature: any, gender: 'male' | 'female') => {
  let slim = {
    defaultImageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/hips/male_slim.png'
        : 'https://cdn.easysize.me/images/shapes/hips/female_slim.png',
    imageTitle: 'Slim',
    imageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/hips/male_slim.png'
        : 'https://cdn.easysize.me/images/shapes/hips/female_slim.png',
  };
  let average = {
    defaultImageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/hips/male_average.png'
        : 'https://cdn.easysize.me/images/shapes/hips/female_average.png',
    imageTitle: 'Average',
    imageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/hips/male_average.png'
        : 'https://cdn.easysize.me/images/shapes/hips/female_average.png',
  };
  let wide = {
    defaultImageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/hips/male_wide.png'
        : 'https://cdn.easysize.me/images/shapes/hips/female_wide.png',
    imageTitle: 'Wide',
    imageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/hips/male_wide.png'
        : 'https://cdn.easysize.me/images/shapes/hips/female_wide.png',
  };

  if (
    !Object.keys(feature).includes('U_HIPSHAPE__MALE') &&
    !Object.keys(feature).includes('U_HIPSHAPE__FEMALE')
  ) {
    return { slim, average, wide };
  }

  let options = feature[
    gender === 'male' ? 'U_HIPSHAPE__MALE' : 'U_HIPSHAPE__FEMALE'
  ]['options'] as any[];

  options.forEach((option) => {
    if (option.key === 'HIP_1-SLIM') {
      slim.imageUrl = option.image;
    }
    if (option.key === 'HIP_2-AVERAGE') {
      average.imageUrl = option.image;
    }
    if (option.key === 'HIP_3-WIDE') {
      wide.imageUrl = option.image;
    }
  });

  return { slim, average, wide };
};

const extractBellyShape = (feature: any, gender: 'male' | 'female') => {
  let flat = {
    defaultImageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/belly/male_flat.png'
        : 'https://cdn.easysize.me/images/shapes/belly/female_flat.png',
    imageTitle: 'Flat',
    imageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/belly/male_flat.png'
        : 'https://cdn.easysize.me/images/shapes/belly/female_flat.png',
  };
  let average = {
    defaultImageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/belly/male_average.png'
        : 'https://cdn.easysize.me/images/shapes/belly/female_average.png',
    imageTitle: 'Average',
    imageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/belly/male_average.png'
        : 'https://cdn.easysize.me/images/shapes/belly/female_average.png',
  };
  let round = {
    defaultImageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/belly/male_round.png'
        : 'https://cdn.easysize.me/images/shapes/belly/female_round.png',
    imageTitle: 'Round',
    imageUrl:
      gender === 'male'
        ? 'https://cdn.easysize.me/images/shapes/belly/male_round.png'
        : 'https://cdn.easysize.me/images/shapes/belly/female_round.png',
  };

  if (
    !Object.keys(feature).includes('U_BELLYSHAPE__MALE') &&
    !Object.keys(feature).includes('U_BELLYSHAPE__FEMALE')
  ) {
    return { flat, average, round };
  }

  let options = feature[
    gender === 'male' ? 'U_BELLYSHAPE__MALE' : 'U_BELLYSHAPE__FEMALE'
  ]['options'] as any[];

  options.forEach((option) => {
    if (option.key === 'BELLY_NS_1-FLAT') {
      flat.imageUrl = option.image;
    }
    if (option.key === 'BELLY_NS_2-AVERAGE') {
      average.imageUrl = option.image;
    }
    if (option.key === 'BELLY_NS_3-ROUND') {
      round.imageUrl = option.image;
    }
  });

  return { flat, average, round };
};

export const mapWidgetResponseToWidgetState = (
  widgetResponse: any
): WidgetState => {
  const { css, data } = widgetResponse;

  const borderRadius = extractCtaBorderRadius(css);
  const backgroundColor = extractCtaBackgroundColor(css);
  const fontColor = extractCtaFontColor(css);
  const text = extractCtaText(data.translations || {});
  const headerFont = extractWidgetHeaderFontFamily(css);
  const bodyFont = extractWidgetBodyFontFamily(css);
  return {
    ctaStyle: { borderRadius, backgroundColor, fontColor, text },
    showEasysizeWatermark: extractESWatermarkStatus(css),
    lengthUnit: extractLengthMesurement(css),
    ctaBehavior: extractCtaBehavior(data.features),
    fontSetting: {
      headerFontColor: extractWidgetHeaderFontColor(css),
      headerFontSize: extractWidgetHeaderFontSize(css),
      headerFontOnlyCapitals: extractWidgetHeaderTextTransform(css),
      headerFontWeight: extractWidgetHeaderFontWeight(css) as FontWeight,
      customHeaderFontName: headerFont.fontFamily,
      customHeaderFontUrl: headerFont.url,
      bodyFontColor: extractWidgetBodyFontColor(css),
      bodyFontOnlyCapitals: extractWidgetBodyTextTransform(css),
      bodyFontSize: extractWidgetBodyFontSize(css),
      bodyFontWeight: extractWidgetBodyFontWeight(css) as FontWeight,
      bodyPadding: extractWidgetBodyAnswerPadding(css),
      bodyBorderHasRadius: extractWidgetBodyAnswerBorderRadius(css),
      customBodyFontName: bodyFont.fontFamily,
      customBodyFontUrl: bodyFont.url,
    },
    widgetBackground: extractWidgetBackground(css),
    maleChestShape: extractChestShape(
      data.questions || {},
      'male'
    ) as ChestShape,
    femaleChestShape: extractChestShape(
      data.questions || {},
      'female'
    ) as ChestShape,
    maleHipShape: extractHipShape(data.questions || {}, 'male') as HipShape,
    femaleHipShape: extractHipShape(data.questions || {}, 'female') as HipShape,
    maleBellyShape: extractBellyShape(
      data.questions || {},
      'male'
    ) as BellyShape,
    femaleBellyShape: extractBellyShape(
      data.questions || {},
      'female'
    ) as BellyShape,
  };
};
